import React, { FC, useEffect } from 'react';
import { Audience, useAuthState, AuthStates } from '@vis-auth/react';
import { EveFooter, EveRoutes, EveRedirect } from '@ver-uds/e-verify';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { EverifyUserRole, User } from '@vis-auth/vis-user-client';
import { AccountType } from 'eve-account-client';
import ReactGA from 'react-ga4';
import { InternalRoutes } from './constants';
import { UserProvider, useUser } from './context/User/UserContext';
import SessionHandler from './components/SessionHandler';
import Header from './components/Header';
import PrivateRoute from './components/PrivateRoute';
import CompanyPage from './components/CompanyProfile';
import ChildPage from './components/ManageCompanies/ChildProfile';
import CorporatePage from './components/CorporateProfile';
import EnrollmentPage from './pages/Enrollment/Enrollment';
import {
  ErrorPage,
  ProfilePage,
  AddUserPage,
  CloseAccountPage,
  EditCompanyInformationPage,
  EditCompanyLocationsPage,
  ManageCompaniesPage,
  EditEmployerCategoryPage,
  EditHiringSitesPage,
  NotFoundPage,
  ViewUsersPage,
} from './pages';
import { EnrollmentType } from './pages/Enrollment/EnrollmentData';
import ClientMouUploadPage from './pages/ClientMou/ClientMouUploadPage';
import CSVGuidelines from './components/HiringSites/BulkUpload/CSVGuidelines';
import MainRoute from './components/MainRoute/MainRoute';
import { useToggles } from './context/Toggles/TogglesContext';

const invalidAccountId = '-1';

const OriginRedirect = ({ path }: { path: string }): null => {
  useEffect(() => {
    window.location.assign(path);
  });
  return null;
};

const NotFoundRedirect = () => {
  if (process.env.NODE_ENV === 'production') {
    return <EveRedirect pageNotFoundPath={InternalRoutes.PAGE_NOT_FOUND} />;
  }
  return <Redirect to={InternalRoutes.PAGE_NOT_FOUND} />;
};

const PageRoutesWithUser: React.FC = () => {
  const toggles = useToggles();
  const isMfaMandatory = toggles?.get('MFA_MANDATORY') === 'on';

  const user = useUser() as User;
  const forceMigratePage = isMfaMandatory && !user?.mfaEnabled;

  if (forceMigratePage) {
    return <OriginRedirect path="/migrate" />;
  }
  return (
    <>
      <Header />

      <Switch>
        <Route path={InternalRoutes.PAGE_NOT_FOUND}>
          <NotFoundPage />
        </Route>

        <MainRoute exact path={[InternalRoutes.PROFILE, '/']}>
          <ProfilePage />
        </MainRoute>

        <MainRoute exact path={InternalRoutes.CSV_GUIDELINES}>
          <CSVGuidelines />
        </MainRoute>

        <PrivateRoute exact path={EveRoutes.ACCOUNT_ENROLLMENT} roles={[EverifyUserRole.ENROLLMENT]}>
          <EnrollmentPage />
        </PrivateRoute>

        <PrivateRoute
          accountTypes={[AccountType.AGENCY]}
          roles={[EverifyUserRole.PROGRAMADMIN, EverifyUserRole.GENERAL]}
          path={EveRoutes.COMPANY_CLIENTS_PREFIX}
        >
          <Switch>
            <MainRoute exact path={EveRoutes.COMPANY_CLIENTS_NEW}>
              <EnrollmentPage enrollmentType={EnrollmentType.CLIENT} />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_CLIENTS_VIEW}>
              <ManageCompaniesPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_CLIENTS_CLIENT_PROFILE_VIEW}>
              <ChildPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_CLIENTS_CLIENT_ADDRESS_EDIT}>
              <EditCompanyLocationsPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_CLIENTS_CLIENT_COMPANY_INFO_EDIT}>
              <EditCompanyInformationPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_CLIENTS_CLIENT_EMPLOYER_CATEGORY_EDIT}>
              <EditEmployerCategoryPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_CLIENTS_CLIENT_HIRING_SITES_EDIT}>
              <EditHiringSitesPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_CLIENTS_CLOSE}>
              <CloseAccountPage companyLocations />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_CLIENTS_CLIENT_MOU_UPLOAD}>
              <ClientMouUploadPage />
            </MainRoute>
            <NotFoundRedirect />
          </Switch>
        </PrivateRoute>

        <PrivateRoute
          accountTypes={[AccountType.CHILD, AccountType.EMPLOYER, AccountType.AGENCY]}
          roles={[EverifyUserRole.PROGRAMADMIN]}
          path="/account/company"
        >
          <Switch>
            <MainRoute exact path={EveRoutes.COMPANY_COMPANY_INFO_EDIT}>
              <EditCompanyInformationPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_EMPLOYER_CATEGORY_EDIT}>
              <EditEmployerCategoryPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_HIRING_SITES_EDIT}>
              <EditHiringSitesPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.COMPANY_ADDRESS_EDIT}>
              <EditCompanyLocationsPage />
            </MainRoute>
            {process.env.NODE_ENV === 'production' ? null : (
              <MainRoute exact path={EveRoutes.COMPANY_USERS_NEW}>
                <AddUserPage />
              </MainRoute>
            )}
            {process.env.NODE_ENV === 'production' ? null : (
              <MainRoute exact path={EveRoutes.COMPANY_USERS}>
                <ViewUsersPage />
              </MainRoute>
            )}

            <MainRoute exact path={EveRoutes.COMPANY_CLOSE}>
              <CloseAccountPage />
            </MainRoute>

            <MainRoute exact path={EveRoutes.COMPANY_PROFILE_VIEW}>
              <CompanyPage />
            </MainRoute>

            <NotFoundRedirect />
          </Switch>
        </PrivateRoute>

        {/* This is here to avoid the route collision for child company profile page routes below */}
        <PrivateRoute
          accountTypes={[AccountType.CORPORATE]}
          roles={[EverifyUserRole.CORPORATEADMIN]}
          path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CLOSE}
        >
          <Switch>
            <MainRoute exact path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CLOSE}>
              <CloseAccountPage companyLocations />
            </MainRoute>
          </Switch>
        </PrivateRoute>

        {/* This is here to avoid the route collision between Add New Company Location and Company Location Profile */}
        <PrivateRoute
          accountTypes={[AccountType.CORPORATE]}
          roles={[EverifyUserRole.CORPORATEADMIN]}
          path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_NEW}
        >
          <Switch>
            <MainRoute exact path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_NEW}>
              <EnrollmentPage enrollmentType={EnrollmentType.CHILD} />
            </MainRoute>
          </Switch>
        </PrivateRoute>

        <PrivateRoute
          accountTypes={[AccountType.CHILD]}
          roles={[EverifyUserRole.CORPORATEADMIN]}
          path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_PREFIX}
        >
          <Switch>
            <MainRoute exact path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_COMPANY_INFO_EDIT}>
              <EditCompanyInformationPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_EMPLOYER_CATEGORY_EDIT}>
              <EditEmployerCategoryPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_HIRING_SITES_EDIT}>
              <EditHiringSitesPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_ADDRESS_EDIT}>
              <EditCompanyLocationsPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_PROFILE_VIEW}>
              <ChildPage />
            </MainRoute>
            <Redirect
              exact
              from={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_PREFIX}
              to={EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_PROFILE_VIEW}
            />
            <NotFoundRedirect />
          </Switch>
        </PrivateRoute>

        <PrivateRoute
          accountTypes={[AccountType.CORPORATE]}
          roles={[EverifyUserRole.CORPORATEADMIN]}
          path="/account/corporate"
        >
          <Switch>
            <MainRoute exact path={EveRoutes.CORPORATE_PROFILE_VIEW}>
              <CorporatePage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.CORPORATE_COMPANY_INFO_EDIT}>
              <EditCompanyInformationPage isCorporateAdmin />
            </MainRoute>
            <MainRoute exact path={EveRoutes.CORPORATE_ADDRESS_EDIT}>
              <EditCompanyLocationsPage isCorporateAdmin />
            </MainRoute>
            <MainRoute exact path={EveRoutes.CORPORATE_COMPANY_LOCATIONS_VIEW}>
              <ManageCompaniesPage />
            </MainRoute>
            <MainRoute exact path={EveRoutes.CORPORATE_CLOSE}>
              <CloseAccountPage />
            </MainRoute>
            <NotFoundRedirect />
          </Switch>
        </PrivateRoute>

        <NotFoundRedirect />
      </Switch>
    </>
  );
};

const PageRoutes: FC = () => {
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
  }, []);

  const { audience, state, user } = useAuthState();

  const accountId = user?.['account_id'] as string;
  const hasValidAccount = !!accountId && accountId !== invalidAccountId;
  const isAuthed = state === AuthStates.authenticated;
  const canFindUser = hasValidAccount && isAuthed;

  const isEnrollmentJWT = audience === Audience.everifyEnrollment;

  if (isEnrollmentJWT) {
    return <OriginRedirect path={EveRoutes.ACCOUNT_ENROLL} />;
  }

  return (
    <>
      <>
        <SessionHandler />
        <Switch>
          <Route exact path={EveRoutes.LOGIN}>
            <OriginRedirect path="/" />
          </Route>

          <Route path="/">
            <ErrorBoundary FallbackComponent={ErrorPage}>
              <Switch>
                {canFindUser && (
                  <UserProvider>
                    <PageRoutesWithUser />
                  </UserProvider>
                )}

                {!canFindUser && (
                  <Switch>
                    <Route path={InternalRoutes.PAGE_NOT_FOUND}>
                      <NotFoundPage />
                    </Route>

                    <NotFoundRedirect />
                  </Switch>
                )}
              </Switch>
            </ErrorBoundary>

            <div className="margin-top-auto">
              <EveFooter />
            </div>
          </Route>
        </Switch>
      </>
    </>
  );
};

export default PageRoutes;
